/**
 * Reel export columns (SPEC §19.2): the OutlierOS 23-column dataset, defined once for CSV, TSV, JSON, Notion
 * Markdown, the extension and MCP. ViralWise adds the spoken hook, virality and transcript script/language.
 * Pure: the caller passes rows, the creator and (optionally) transcripts.
 */
import type { ReportReel } from './report';
import { engagement } from './report';
import { TIER_LABELS, formatWiseScore, tierFor, type TierThresholds } from './score';

export interface ExportTranscript { text: string; romanText: string | null; script: string; lang: string; hook: string | null }
export interface ExportCreator { handle: string; fullName: string | null }

export const EXPORT_COLUMNS = [
  'Caption', 'Post URL', 'Thumbnail URL', 'Video URL', 'Platform', 'Post Type', 'Creator Handle', 'Creator Name', 'Views', 'Wise Score',
  'Wise Score Tier', 'Virality (0-100)', 'Likes', 'Comments', 'Engagement Rate (%)', 'Likes-to-Views (%)', 'Comments-to-Views (%)',
  'Velocity (Views/Day)', 'Duration (s)', 'Post Date', 'Post Time', 'Day of Week', 'Hour of Day', 'Mentions', 'Hashtags',
  'Audio', 'Collab', 'Paid Partnership', 'Spoken Hook', 'Transcript', 'Transcript (Roman)', 'Transcript Language',
] as const;

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const pct = (n: number | null) => (n === null ? '' : Number((n * 100).toFixed(2)));
const ratio = (a: number | null, b: number | null) => (a === null || !b ? null : a / b);

/** The tier's display name (decision 62: Legendary, Viral, Hot, Above usual, Normal), '' when not scored. */
export function tierLabel(score: number | null, t: TierThresholds): string {
  const tier = tierFor(score, t);
  return tier ? TIER_LABELS[tier] : '';
}

/** Local date/time parts in a time zone (IST by default for Indian creators; the UI passes the viewer's). */
function parts(ms: number, timeZone: string) {
  const f = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23', weekday: 'long' });
  const p = Object.fromEntries(f.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}:${p.second}`, day: p.weekday ?? DAYS[new Date(ms).getUTCDay()]!, hour: Number(p.hour) % 24 };
}

export function exportRow(r: ReportReel, c: ExportCreator, opts: { tiers: TierThresholds; timeZone: string; now: number; transcript?: ExportTranscript | null }) {
  const t = r.postedAt !== null ? parts(r.postedAt, opts.timeZone) : null;
  const days = r.postedAt !== null ? Math.max(0.1, (opts.now - r.postedAt) / 86_400_000) : null;
  const mentions = (r.caption?.match(/@[A-Za-z0-9._]{1,30}/g) ?? []).join(', ');
  const url = `https://www.instagram.com/reel/${r.shortcode}/`;
  return [
    // Handle without "@": spreadsheets would read "@…" as a formula.
    r.caption ?? '', url, r.thumbUrl ?? '', r.videoUrl ?? '', 'Instagram', 'reel', c.handle, c.fullName ?? c.handle, r.views ?? '',
    r.outlierScore !== null ? Number(r.outlierScore.toFixed(2)) : '', tierLabel(r.outlierScore, opts.tiers), r.viralityScore ?? '',
    r.likesHidden ? '' : r.likes ?? '', r.comments ?? '', pct(engagement(r)), pct(ratio(r.likesHidden ? null : r.likes, r.views)),
    pct(ratio(r.comments, r.views)), r.views && days ? Math.round(r.views / days) : '', r.durationS ?? '', t?.date ?? '', t?.time ?? '',
    t?.day ?? '', t?.hour ?? '', mentions, r.hashtags.map((h) => `#${h}`).join(' '),
    r.audio ? (r.audio.title ?? (r.audio.type === 'original' ? 'Original audio' : '')) + (r.audio.artist ? ` · ${r.audio.artist}` : '') : '',
    r.isCollab ? 'Yes' : 'No', r.isPaidPartnership ? 'Yes' : 'No', opts.transcript?.hook ?? r.hook ?? '', opts.transcript?.text ?? '',
    opts.transcript?.romanText ?? '', opts.transcript?.lang ?? '',
  ];
}

export function exportJson(rows: ReportReel[], c: ExportCreator, opts: { tiers: TierThresholds; timeZone: string; now: number; transcripts?: Record<string, ExportTranscript> }) {
  return rows.map((r) => Object.fromEntries(EXPORT_COLUMNS.map((col, i) => [col, exportRow(r, c, { ...opts, transcript: opts.transcripts?.[r.pk] })[i]])));
}

/** Notion Markdown (OutlierOS extension format): title, summary quote, then a section per reel. */
export function toNotionMarkdown(rows: ReportReel[], c: ExportCreator, opts: { brand: string; now: number; transcripts?: Record<string, ExportTranscript> }): string {
  const fmt = (n: number | null) => (n === null ? '–' : n.toLocaleString('en-US'));
  const out: string[] = [
    `# ⚡ ${opts.brand} report: @${c.handle}`,
    '',
    `> **Creator:** ${c.fullName ?? c.handle} (@${c.handle})  `,
    `> **Generated:** ${new Date(opts.now).toISOString().slice(0, 10)}  `,
    `> **Reels:** ${rows.length}`,
    '',
    '## 📊 Top reels and spoken hooks',
    '',
  ];
  rows.forEach((r, i) => {
    const t = opts.transcripts?.[r.pk];
    const hook = t?.hook ?? r.hook ?? (t?.text ? t.text.slice(0, 120) : null);
    out.push(`### ${i + 1}. [Reel ${r.shortcode}](https://www.instagram.com/reel/${r.shortcode}/): **${r.outlierScore !== null ? formatWiseScore(r.outlierScore) : 'not scored'}** (${fmt(r.views)} views)`, '');
    out.push('| Wise Score | Views | Likes | Published |', '|---|---|---|---|');
    out.push(`| ${r.outlierScore !== null ? `${r.outlierScore.toFixed(2)}×` : '–'} | ${fmt(r.views)} | ${r.likesHidden ? 'hidden' : fmt(r.likes)} | ${r.postedAt ? new Date(r.postedAt).toISOString().slice(0, 10) : '–'} |`, '');
    out.push(`> 🎙️ **Spoken hook:** ${hook ?? 'Transcript pending'}`, '');
    if (t?.text) out.push('<details><summary>📜 Full transcript</summary>', '', t.text, '', '</details>', '');
    if (r.caption) out.push('<details><summary>📝 Caption</summary>', '', r.caption, '', '</details>', '');
    out.push('---', '');
  });
  return out.join('\n');
}
